import React, { useMemo } from 'react';
import loadable from '@loadable/component';

import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { camelize } from '../../util/string';
import { propTypes } from '../../util/types';

import FallbackPage from './FallbackPage';
import { ASSET_NAME } from './LandingPage.duck';
import HeroSection from './HeroSection/HeroSection';
// Lazy-load TrustedLocalRentalsSection to avoid SSR issues with swiper ES module
const TrustedLocalRentalsSection = loadable(() =>
  import(/* webpackChunkName: "TrustedLocalRentalsSection" */ './TrustedLocalRentalsSection/TrustedLocalRentalsSection')
);

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

export const LandingPageComponent = props => {
  const { pageAssetsData, inProgress, error } = props;

  const landingPageData = pageAssetsData?.[camelize(ASSET_NAME)]?.data;

  const pageAssetsWithCustomHero = useMemo(() => {
    if (!landingPageData?.sections) {
      return landingPageData;
    }

    const hasTrustedSection = landingPageData.sections.some(
      section => section?.sectionType === 'trustedLocalRentals'
    );

    let heroAdjusted = false;
    let trustedInjected = false;

    const patchedSections = [];

    landingPageData.sections.forEach(section => {
      if (!section) {
        patchedSections.push(section);
        return;
      }

      const { sectionId, sectionType } = section;

      if (sectionId === 'hero') {
        if (sectionType !== 'hero') {
          patchedSections.push({ ...section, sectionType: 'hero' });
          heroAdjusted = true;
        } else {
          patchedSections.push(section);
        }

        if (!hasTrustedSection && !trustedInjected) {
          patchedSections.push({
            sectionId: 'trusted-local-rentals',
            sectionType: 'trustedLocalRentals',
          });
          trustedInjected = true;
        }

        return;
      }

      patchedSections.push(section);
    });

    if (!hasTrustedSection && !trustedInjected) {
      patchedSections.unshift({
        sectionId: 'trusted-local-rentals',
        sectionType: 'trustedLocalRentals',
      });
      trustedInjected = true;
    }

    return heroAdjusted || trustedInjected
      ? { ...landingPageData, sections: patchedSections }
      : landingPageData;
  }, [landingPageData]);

  const pageBuilderOptions = useMemo(
    () => ({
      sectionComponents: {
        hero: { component: HeroSection },
        trustedLocalRentals: { component: TrustedLocalRentalsSection },
      },
    }),
    []
  );

  return (
    <PageBuilder
      pageAssetsData={pageAssetsWithCustomHero}
      inProgress={inProgress}
      error={error}
      fallbackPage={<FallbackPage error={error} />}
      stagedRender={true}
      stagedInitialCount={2}
      options={pageBuilderOptions}
    />
  );
};

LandingPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
  error: propTypes.error,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  return { pageAssetsData, inProgress, error };
};

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const LandingPage = compose(connect(mapStateToProps))(LandingPageComponent);

export default LandingPage;
